// UpcomingEvents.js
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box, Typography, Card, CardContent, List, ListItem,
  ListItemIcon, ListItemText, Chip, useTheme, Button
} from "@mui/material";
import {
  CalendarMonth, ArrowForward, Circle
} from "@mui/icons-material";
import { useCollection } from "../../hooks/useCollection";
import { useAuth } from "../../hooks/useAuth";
import { EVENT_TYPE_COLORS } from "../../utils/models/calendarModel";
import LoadingSpinner from "../ui/LoadingSpinner";
import EmptyState from "../ui/EmptyState";

const UpcomingEvents = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { data: events, loading } = useCollection("calendarEvents");

  const upcomingEvents = useMemo(() => {
    if (!events) return [];
    
    const now = new Date();
    const threeDaysFromNow = new Date();
    threeDaysFromNow.setDate(threeDaysFromNow.getDate() + 3);
    threeDaysFromNow.setHours(23, 59, 59, 999);

    return events
      .filter(event => {
        const eventStart = new Date(event.startDate);
        return (
          event.isActive !== false &&
          eventStart >= now &&
          eventStart <= threeDaysFromNow
        );
      })
      .sort((a, b) => new Date(a.startDate) - new Date(b.startDate))
      .slice(0, 5); // Máximo 5 eventos
  }, [events]);

  const formatEventDate = (dateStr, allDay) => {
    const date = new Date(dateStr);
    const today = new Date();
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);

    let dayLabel = "";
    if (date.toDateString() === today.toDateString()) {
      dayLabel = "Hoy";
    } else if (date.toDateString() === tomorrow.toDateString()) {
      dayLabel = "Mañana";
    } else {
      dayLabel = date.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "short" });
    }

    if (allDay) return `${dayLabel} - Todo el día`;
    return `${dayLabel} - ${date.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" })}`;
  };

  if (loading) return <LoadingSpinner />;

  return (
    <Card
      sx={{
        height: "100%",
        borderTop: 4,
        borderColor: "#00c07c",
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: theme.shadows[8],
          transform: { xs: "none", sm: "translateY(-4px)" },
        },
      }}
    >
      <CardContent sx={{ p: { xs: 1.5, sm: 2, md: 2.5 } }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: { xs: 1, sm: 1.5 }, mb: { xs: 1.5, sm: 2, md: 2.5 } }}>
          <Box
            sx={{
              width: { xs: 40, sm: 48 },
              height: { xs: 40, sm: 48 },
              borderRadius: 2,
              background: "linear-gradient(135deg, #00e1d2 0%, #00c07c 100%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CalendarMonth sx={{ color: "white", fontSize: { xs: 20, sm: 24 } }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={600} sx={{ fontSize: { xs: '0.95rem', sm: '1.1rem', md: '1.25rem' } }}>
              Próximos Eventos
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}>
              {upcomingEvents.length > 0 
                ? `${upcomingEvents.length} evento${upcomingEvents.length > 1 ? 's' : ''} los próximos 3 días`
                : "Sin eventos próximos"}
            </Typography>
          </Box>
        </Box>

        {upcomingEvents.length === 0 ? (
          <EmptyState
            title="Sin eventos"
            description="No tienes eventos en los próximos 3 días"
            compact
          />
        ) : (
          <List dense disablePadding>
            {upcomingEvents.map((event) => (
              <ListItem
                key={event.id}
                disablePadding
                sx={{ mb: 1 }}
              >
                <Box
                  sx={{
                    width: "100%",
                    p: { xs: 1, sm: 1.5 },
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#e2e8f0',
                    borderLeft: 4,
                    borderLeftColor: event.color || EVENT_TYPE_COLORS[event.eventType] || "#1976d2",
                    transition: "all 0.2s ease",
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255,255,255,0.03)' 
                        : 'rgba(25, 118, 210, 0.03)',
                    },
                  }}
                  onClick={() => navigate("/calendar")}
                >
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 1 }}>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: { xs: '0.75rem', sm: '0.85rem' }, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {event.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                        {formatEventDate(event.startDate, event.allDay)}
                      </Typography>
                      {event.createdByName && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: { xs: '0.6rem', sm: '0.7rem' } }}>
                          {event.createdByName}
                        </Typography>
                      )}
                    </Box>
                    <Chip
                      label={event.eventType === "meeting" ? "Reunión" : 
                             event.eventType === "task" ? "Tarea" :
                             event.eventType === "deadline" ? "Límite" :
                             event.eventType === "call" ? "Llamada" :
                             event.eventType === "reminder" ? "Recordatorio" : "Otro"}
                      size="small"
                      sx={{
                        fontSize: { xs: "0.55rem", sm: "0.65rem" },
                        height: { xs: 20, sm: 22 },
                        backgroundColor: event.color || EVENT_TYPE_COLORS[event.eventType],
                        color: "white",
                        flexShrink: 0,
                      }}
                    />
                  </Box>
                </Box>
              </ListItem>
            ))}
          </List>
        )}

        <Button
          endIcon={<ArrowForward />}
          onClick={() => navigate("/calendar")}
          sx={{
            mt: 1,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: "12px",
            fontSize: { xs: '0.75rem', sm: '0.85rem' },
          }}
          fullWidth
        >
          Ver calendario completo
        </Button>
      </CardContent>
    </Card>
  );
};

export default UpcomingEvents;